import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, ArrowRight } from "lucide-react";
import axios from "axios";
import { getImageUrl } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo-sciad.png";

const SoftwarePage = () => {
  const { slug } = useParams();
  const [software, setSoftware] = useState(null);
  const [allSoftware, setAllSoftware] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [swRes, allRes] = await Promise.all([
          axios.get(`${API}/software/${slug}`),
          axios.get(`${API}/software`)
        ]);
        setSoftware(swRes.data);
        setAllSoftware(allRes.data.filter(s => s.slug !== slug));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1121] flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#00CCC2] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!software) {
    return (
      <div className="min-h-screen bg-[#0B1121] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Logiciel non trouvé</h1>
          <Link to="/" className="text-[#00CCC2] hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1121]" data-testid="software-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="SCIAD" className="h-12 w-auto" />
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à l'accueil
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {software.hero_image_url && (
          <>
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-20"
              style={{ backgroundImage: `url(${getImageUrl(software.hero_image_url)})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0B1121] via-transparent to-[#0B1121]" />
          </>
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <p className="text-sm uppercase tracking-[0.3em] text-[#00CCC2] mb-4">
            {software.technologies?.join(" • ")}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            <span className="gradient-text">{software.name}</span>
          </h1>
          <p className="text-xl sm:text-2xl text-slate-300 mb-6 max-w-3xl">
            {software.tagline}
          </p>
          <p className="text-lg text-slate-400 mb-8 max-w-3xl">
            {software.description}
          </p>
          
          {/* Features list */}
          <div className="flex flex-wrap gap-4 mb-10">
            {software.features?.map((feature, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#131B2F] border border-white/10">
                <Check className="w-4 h-4 text-[#00CCC2]" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>

          <Link
            to={`/demo?logiciel=${encodeURIComponent(software.name)}`}
            className="inline-block px-8 py-4 btn-primary rounded-full text-lg"
            data-testid="software-cta"
          >
            {software.cta_text || "Demander une démo"}
          </Link>
        </div>
      </section>

      {/* Sections */}
      {software.sections?.map((section, index) => (
        <section 
          key={index} 
          className={`py-20 ${index % 2 === 0 ? 'bg-[#131B2F]' : 'bg-[#0B1121]'}`}
          data-testid={`software-section-${index}`}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-6">
                  <span className="gradient-text">{section.title}</span>
                </h2>
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  {section.description}
                </p>
                
                {section.features && section.features.length > 0 && (
                  <ul className="space-y-4">
                    {section.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="p-1 rounded-full bg-[#00CCC2]/20 mt-1">
                          <Check className="w-4 h-4 text-[#00CCC2]" />
                        </div>
                        <span className="text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                {section.image_url ? (
                  <div className="rounded-2xl overflow-hidden border border-white/10">
                    <img 
                      src={getImageUrl(section.image_url)} 
                      alt={section.title}
                      className="w-full h-auto"
                    />
                  </div>
                ) : (
                  <div className="aspect-video rounded-2xl bg-gradient-to-br from-[#00CCC2]/20 to-[#2395E3]/20 border border-white/10 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-6xl mb-4 opacity-50">📊</div>
                      <p className="text-slate-400">Illustration</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#00CCC2]/10 to-[#2395E3]/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Prêt à transformer votre activité ?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Contactez-nous pour une démonstration personnalisée de {software.name}
          </p>
          <Link
            to={`/demo?logiciel=${encodeURIComponent(software.name)}`}
            className="inline-block px-10 py-5 btn-primary rounded-full text-xl"
          >
            Demander une démo gratuite
          </Link>
        </div>
      </section>

      {/* Other Software */}
      {allSoftware.length > 0 && (
        <section className="py-20 bg-[#0B1121]">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-2xl sm:text-3xl font-bold mb-10 text-center">
              Découvrez nos autres solutions
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {allSoftware.slice(0, 3).map((sw) => (
                <Link
                  key={sw.id}
                  to={`/logiciel/${sw.slug}`}
                  className="group p-6 rounded-2xl bg-[#131B2F] border border-white/10 card-hover"
                  data-testid={`other-software-${sw.slug}`}
                >
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-[#00CCC2] transition-colors">
                    {sw.name}
                  </h3>
                  <p className="text-slate-400 mb-4 line-clamp-2">{sw.description}</p>
                  <div className="flex items-center gap-2 text-[#00CCC2]">
                    En savoir plus <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-8 bg-[#0B1121] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 text-center text-slate-400 text-sm">
          © {new Date().getFullYear()} SCIAD - Un client, un projet. Tous droits réservés.
        </div>
      </footer>
    </div>
  );
};

export default SoftwarePage;
